import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSupabase } from './SupabaseContext'
import { toast } from 'sonner'
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Alert,
} from '@mui/material'

import { styled, createTheme, ThemeProvider } from '@mui/material/styles'

import WarningRoundedIcon from '@mui/icons-material/WarningRounded'

// Custom theme matching your app colors

const theme = createTheme({
  palette: {
    primary: {
      main: '#ffda1b', // gcc-gold

      contrastText: '#232536', // gcc-dark
    },

    secondary: {
      main: '#232536', // gcc-dark

      contrastText: '#ffffff',
    },

    background: {
      default: '#f8fafc', // gray-50

      paper: '#ffffff',
    },

    text: {
      primary: '#111827', // gray-900

      secondary: '#4b5563', // gray-600
    },

    error: {
      main: '#ef4444', // red-500

      light: '#fef2f2', // red-50
    },
  },

  typography: {
    fontFamily: 'Inter, sans-serif',

    h5: {
      fontSize: '1.25rem',

      fontWeight: 600,

      lineHeight: 1.2,
    },

    body1: {
      fontSize: '1rem',

      lineHeight: 1.5,
    },

    body2: {
      fontSize: '0.875rem',

      lineHeight: 1.4,
    },
  },

  shape: {
    borderRadius: 16,
  },
})

// Styled components

const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',

  alignItems: 'center',

  justifyContent: 'center',

  minHeight: '100vh',

  background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
}))

const ContentCard = styled(Paper)(({ theme }) => ({
  textAlign: 'center',

  backgroundColor: '#ffffff',

  padding: theme.spacing(4),

  borderRadius: theme.spacing(2),

  boxShadow:
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',

  border: '1px solid #e5e7eb',

  maxWidth: '28rem',

  margin: theme.spacing(0, 2),

  [theme.breakpoints.up('sm')]: {
    margin: 0,
  },
}))

const ErrorCard = styled(Paper)(({ theme }) => ({
  textAlign: 'center',

  backgroundColor: '#ffffff',

  padding: theme.spacing(4),

  borderRadius: theme.spacing(2),

  boxShadow:
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',

  border: '1px solid #fecaca', // red-200

  maxWidth: '28rem',

  margin: theme.spacing(0, 2),

  [theme.breakpoints.up('sm')]: {
    margin: 0,
  },
}))

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: '#ffda1b', // gcc-gold

  marginBottom: theme.spacing(2),
}))

const ErrorIconWrapper = styled(Box)(({ theme }) => ({
  width: '3rem',

  height: '3rem',

  backgroundColor: '#fef2f2', // red-50

  borderRadius: '50%',

  display: 'flex',

  alignItems: 'center',

  justifyContent: 'center',

  margin: '0 auto',

  marginBottom: theme.spacing(2),
}))
const BackButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#232536', // gcc-dark

  color: 'white',

  padding: theme.spacing(1, 3),

  borderRadius: theme.spacing(1),

  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: '#ffda1b', // gcc-gold

    color: '#232536', // gcc-dark
  },
}))
const AuthCallbackHandler = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const { supabase } = useSupabase()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check for OAuth errors in URL parameters first
        const error = searchParams.get('error')
        const errorDescription = searchParams.get('error_description')
        const errorCode = searchParams.get('error_code')

        // Handle OAuth consent denial or other errors
        if (error) {
          console.error('OAuth Error detected:', {
            error,
            errorDescription,
            errorCode,
          })

          // Handle specific OAuth errors
          if (error === 'access_denied') {
            toast.error(
              'Sign-in was cancelled. Please try again if you want to register.'
            )
            // Clear stored form data since user cancelled
            sessionStorage.removeItem('pendingFormData')
            sessionStorage.removeItem('ipCity')
            navigate('/', { replace: true })
            return
          } else if (error === 'invalid_request') {
            toast.error(
              'There was an issue with the sign-in request. Please try again.'
            )
            navigate('/', { replace: true })
            return
          } else {
            toast.error(`Sign-in failed: ${errorDescription || error}`)
            navigate('/', { replace: true })
            return
          }
        }

        // Check if user is authenticated
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession()

        if (sessionError) {
          console.error('Session error:', sessionError)
          throw sessionError
        }

        // If no session after OAuth callback, user likely denied consent
        if (!session) {
          console.log(
            'No session found after OAuth callback - user likely denied consent'
          )
          toast.error(
            'Sign-in was not completed. Please try again if you want to register.'
          )
          // Clear stored form data
          sessionStorage.removeItem('pendingFormData')
          sessionStorage.removeItem('ipCity')
          navigate('/', { replace: true })
          return
        }

        const { user } = session

        // Validate that we have essential user data
        if (!user || !user.id || !user.email) {
          console.error('Invalid user data received:', user)
          toast.error(
            'Invalid user information received. Please try signing in again.'
          )
          navigate('/', { replace: true })
          return
        }

        // console.log('Valid user session found:', {
        //   id: user.id,
        //   email: user.email,
        //   provider: user.app_metadata?.provider,
        // })

        // First, check if user is already registered using the UNIQUE constraint on user_id
        const { data: existingUser, error: checkError } = await supabase
          .from('waitlist')
          .select('user_id, email, signed_up_at, full_name')
          .eq('user_id', user.id)
          .single()

        if (checkError && checkError.code !== 'PGRST116') {
          // PGRST116 means no rows returned, which is fine for new users
          console.error('Error checking existing user:', checkError)
        }

        // Get the stored form data
        const pendingFormDataString = sessionStorage.getItem('pendingFormData')

        if (existingUser) {
          // User already exists - show welcome back message
          console.info('User already registered:', existingUser)

          if (pendingFormDataString) {
            // Update with new form data if provided
            const pendingFormData = JSON.parse(pendingFormDataString)
            const userMeta = user.user_metadata || {}

            // Normalize names from different providers
            const firstName =
              pendingFormData.first_name ||
              userMeta.given_name ||
              userMeta.first_name ||
              (userMeta.name ? userMeta.name.split(' ')[0] : '')

            const lastName =
              pendingFormData.last_name ||
              userMeta.family_name ||
              userMeta.last_name ||
              (userMeta.name && userMeta.name.includes(' ')
                ? userMeta.name.split(' ').slice(1).join(' ')
                : '')

            const fullName =
              pendingFormData.name ||
              userMeta.name ||
              userMeta.full_name ||
              `${firstName} ${lastName}`.trim()

            const profilePicture = userMeta.avatar_url || userMeta.picture || ''

            const { error: updateError } = await supabase
              .from('waitlist')
              .update({
                // Update fields with new data, but keep original signed_up_at
                email: user.email, // Keep email updated in case it changed
                full_name: fullName || existingUser.full_name,
                first_name: firstName,
                last_name: lastName,
                phone: pendingFormData.phone || '',
                city: pendingFormData.city || '',
                city_using_ip_addr: sessionStorage.getItem('ipCity') || '',
                company: pendingFormData.company || '',
                country: pendingFormData.country || '',
                avatar_url: profilePicture,
                locale: userMeta.locale || '',
                // Update metadata with login info
                user_metadata: {
                  provider_metadata: userMeta,
                  last_login: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                },
              })
              .eq('user_id', user.id)

            if (updateError) {
              console.error('Error updating user profile:', updateError)
              toast.success('Welcome back! Your profile has been updated.')
            } else {
              toast.success('Welcome back! Your information has been updated.')
            }
          } else {
            // No new form data, just welcome back
            const registrationDate = new Date(
              existingUser.signed_up_at
            ).toLocaleDateString()
            toast.success(
              `Welcome back${
                existingUser.full_name ? `, ${existingUser.full_name}` : ''
              }! You registered on ${registrationDate}.`
            )
          }
        } else {
          // New user registration
          if (pendingFormDataString) {
            const pendingFormData = JSON.parse(pendingFormDataString)
            const userMeta = user.user_metadata || {}

            // Validate that we have required form data
            if (
              !pendingFormData.company?.trim() ||
              !pendingFormData.city?.trim()
            ) {
              console.error('Missing required form data:', pendingFormData)
              toast.error(
                'Missing required information. Please fill out the form again.'
              )
              navigate('/', { replace: true })
              return
            }

            // Normalize names from different providers
            const firstName =
              pendingFormData.first_name ||
              userMeta.given_name ||
              userMeta.first_name ||
              (userMeta.name ? userMeta.name.split(' ')[0] : '')

            const lastName =
              pendingFormData.last_name ||
              userMeta.family_name ||
              userMeta.last_name ||
              (userMeta.name && userMeta.name.includes(' ')
                ? userMeta.name.split(' ').slice(1).join(' ')
                : '')

            const fullName =
              pendingFormData.name ||
              userMeta.name ||
              userMeta.full_name ||
              `${firstName} ${lastName}`.trim()

            const profilePicture = userMeta.avatar_url || userMeta.picture || ''

            // Create new user record - using INSERT since user doesn't exist
            const { error: profileError } = await supabase
              .from('waitlist')
              .insert({
                user_id: user.id,
                email: user.email,
                full_name: fullName,
                first_name: firstName,
                last_name: lastName,
                phone: pendingFormData.phone || '',
                city: pendingFormData.city || '',
                city_using_ip_addr: sessionStorage.getItem('ipCity') || '',
                company: pendingFormData.company || '',
                country: pendingFormData.country || '',
                avatar_url: profilePicture,
                locale: userMeta.locale || '',
                // signed_up_at will be set automatically by DEFAULT NOW()
                user_metadata: {
                  provider_metadata: userMeta,
                  registration_date: new Date().toISOString(),
                  registration_source: 'waitlist_form',
                },
              })

            if (profileError) {
              console.error('Error saving user profile:', profileError)

              // Check if it's a duplicate error
              if (profileError.code === '23505') {
                // PostgreSQL unique constraint violation
                toast.success('Welcome back! You were already registered.')
              } else {
                toast.error(
                  'Account created but failed to save your profile information.'
                )
              }
            } else {
              // console.log('Successfully registered new user')
              toast.success('Registration successful! Welcome to GCC Tax Laws!')
            }
          } else {
            // No form data but new user authenticated - this shouldn't happen in normal flow
            console.warn('New user authenticated without form data')
            toast.success('Registration successful!')
          }
        }

        // Clear the stored data regardless of success
        sessionStorage.removeItem('pendingFormData')
        sessionStorage.removeItem('ipCity')

        // Navigate to success page
        navigate('/success', { replace: true })
      } catch (error) {
        console.error('Auth callback error:', error)
        setError(error.message)

        // Handle specific database errors gracefully
        if (error.code === '23505') {
          // Unique constraint violation
          toast.success('Welcome back! You were already registered.')
          navigate('/success', { replace: true })
        } else {
          toast.error('Authentication failed. Please try again.')
          navigate('/', { replace: true })
        }
      } finally {
        // Always set loading to false
        setLoading(false)
      }
    }

    handleAuthCallback()
  }, [navigate, supabase, searchParams])

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <MainContainer>
          <ContentCard>
            <StyledCircularProgress size={48} thickness={4} />

            <Typography
              variant='h5'
              component='h1'
              sx={{
                color: 'text.primary',

                mb: 1,
              }}
            >
              Completing your registration...
            </Typography>

            <Typography
              variant='body1'
              sx={{
                color: 'text.secondary',

                mb: 2,
              }}
            >
              Please wait while we process your information
            </Typography>

            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
              }}
            >
              This should only take a moment
            </Typography>
          </ContentCard>
        </MainContainer>
      </ThemeProvider>
    )
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <MainContainer>
          <ErrorCard>
            <ErrorIconWrapper>
              <WarningRoundedIcon
                sx={{
                  fontSize: '1.5rem',

                  color: '#dc2626', // red-600
                }}
              />
            </ErrorIconWrapper>

            <Typography
              variant='h5'
              component='h1'
              sx={{
                color: 'text.primary',

                mb: 1,
              }}
            >
              Registration Error
            </Typography>

            <Typography
              variant='body1'
              sx={{
                color: 'text.secondary',

                mb: 3,
              }}
            >
              {error}
            </Typography>

            <BackButton
              onClick={() => navigate('/', { replace: true })}
              variant='contained'
              disableElevation
            >
              Back to Home
            </BackButton>
          </ErrorCard>
        </MainContainer>
      </ThemeProvider>
    )
  }

  return null
}

export default AuthCallbackHandler
